/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  CandidateMatch,
  ClassifiedRequirement,
  CvMatchResult,
  CvSearchResult,
  LockedCandidate,
  PipelineFailureReason,
  RequirementEvidence,
  RoleCandidatePool,
  RoleRequirement,
  TenderDocument,
} from "../../types"
import type { GraphFileItem, UserDocument } from "@/api/models"
import type { TFunction } from "i18next"

import { downloadGraphFileApi, getCitationFilePath } from "@/api/api"
import { downloadCvMatchDocx, getTenderDocumentUrl } from "@/api/tender-api"
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  FileDown,
  FileText,
  Loader2,
  Lock,
  MessageSquare,
  Paperclip,
  RotateCcw,
  Search,
  Star,
  StopCircle,
  Trash2,
  Upload,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { useConfigQuery } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import {
  useAbortCvMatch,
  useAnalyseDocForCvMatch,
  useAttachCvFromLibrary,
  useClassifyCvRequirements,
  useCvLibrarySearch,
  useCvMatchResult,
  useRemoveCandidate,
  useResetCvMatch,
  useStartCvMatch,
  useUpdateLockedCandidates,
} from "../../hooks/use-cv-match"
import { filterAllowedFiles, TENDER_ACCEPT_FILTER } from "../../tender-upload-constants"
import { detectCvLibrary } from "../../utils/detect-cv-library"
import { TenderAiDisclaimer } from "../tender-ai-disclaimer"

// ── Table layout constants ────────────────────────────────────────────────────
/** Width of each requirement column in pixels. Change here to resize all columns. */
const COL_WIDTH_PX = 110
function getPipelineFailureMessage(
  t: (key: string) => string,
  failureReason?: PipelineFailureReason,
  error?: string
) {
  if (failureReason === "user_cancelled") return t("tender.abort.failureReasons.user_cancelled")
  if (failureReason === "worker_lost") return t("tender.abort.failureReasons.worker_lost")
  if (failureReason === "hard_timeout") return t("tender.abort.failureReasons.hard_timeout")
  if (failureReason === "rate_limited_exhausted")
    return t("tender.abort.failureReasons.rate_limited_exhausted")
  return error ?? t("tender.abort.failureReasons.pipeline_error")
}

/** Padding applied to all table cells. Change here to adjust density. */
const CELL_PADDING = "px-1 py-2"

// ── Text normalization for requirement lookups ───────────────────────────────
// LLM passes may produce slightly different whitespace, hyphens, or trailing
// punctuation for the same requirement. Normalize before using as map keys.
function normalizeReqText(text: string): string {
  return text
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-") // normalize dashes
    .toLowerCase()
}

// ── Evidence lookup helper ────────────────────────────────────────────────────

type EvidenceLookup = {
  byId: Map<string, RequirementEvidence>
  byText: Map<string, RequirementEvidence>
}

function buildEvidenceLookup(evidence: RequirementEvidence[] | undefined): EvidenceLookup {
  const byId = new Map<string, RequirementEvidence>()
  const byText = new Map<string, RequirementEvidence>()
  for (const ev of evidence ?? []) {
    if (ev.requirementId) byId.set(ev.requirementId, ev)
    byText.set(normalizeReqText(ev.requirementText), ev)
  }
  return { byId, byText }
}

function lookupEvidence(
  lookup: EvidenceLookup,
  requirementId: string | undefined,
  normText: string
): RequirementEvidence | undefined {
  if (requirementId) {
    return lookup.byId.get(requirementId) ?? lookup.byText.get(normText)
  }
  return lookup.byText.get(normText)
}

function buildNormalizedSet(items: string[]): Set<string> {
  return new Set(items.map(normalizeReqText))
}

// ── Requirement cell icon ─────────────────────────────────────────────────────

/** Status icon used inside cells and evidence popovers. */
function ReqStatusIcon({
  matched,
  overqualified,
  className,
}: {
  matched: boolean
  overqualified: boolean
  className?: string
}) {
  if (!matched) {
    return <X className={cn("text-muted-foreground/40 h-4 w-4", className)} />
  }
  if (overqualified) {
    return (
      <span className={cn("text-warning flex w-fit items-center gap-0.5", className)}>
        <Check className="h-4 w-4" />
        <span className="text-[10px] leading-none font-semibold">↑</span>
      </span>
    )
  }
  return <Check className={cn("text-success h-4 w-4", className)} />
}

// ── Evidence popover ──────────────────────────────────────────────────────────

type EvidencePopoverProps = {
  evidence: RequirementEvidence
  caseId: string
  cvLibraryId: string
  onOpenSource: (ev: RequirementEvidence) => void
}

function EvidencePopover({ evidence, onOpenSource }: EvidencePopoverProps) {
  const { t } = useTranslation()
  const matched = evidence.status !== "missing"
  const overqualified = evidence.status === "overqualified"

  let statusLabel = t("tender.cvMatch.evidence.missing")
  if (overqualified) statusLabel = t("tender.cvMatch.evidence.overqualified")
  else if (matched) statusLabel = t("tender.cvMatch.evidence.met")

  let statusClass = "bg-destructive/10 text-destructive border-destructive"
  if (overqualified) statusClass = "bg-warning/10 text-warning-foreground border-warning"
  else if (matched) statusClass = "bg-success/10 text-success border-success"

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="mx-auto size-8 p-0 hover:opacity-70"
          size="icon"
          type="button"
          variant="ghost"
        >
          <ReqStatusIcon matched={matched} overqualified={overqualified} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-80 p-0 text-sm" side="top">
        <div className="space-y-3 p-3">
          {/* Requirement text + status badge */}
          <div className="flex items-start justify-between gap-2">
            <p className="text-foreground min-w-0 flex-1 text-xs leading-snug font-medium">
              {evidence.requirementText}
            </p>
            <span
              className={cn(
                "shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium",
                statusClass
              )}
            >
              {statusLabel}
            </span>
          </div>

          {/* Reasoning */}
          {evidence.reasoning && (
            <div>
              <p className="text-muted-foreground mb-1 flex items-center gap-1 text-[10px] font-semibold tracking-wide uppercase">
                <MessageSquare className="h-3 w-3" />
                {t("tender.cvMatch.evidence.reasoning")}
              </p>
              <p className="text-foreground/80 text-xs leading-relaxed">{evidence.reasoning}</p>
            </div>
          )}

          {/* Source quote */}
          {evidence.sourceQuote && (
            <blockquote className="border-muted-foreground/30 text-muted-foreground border-l-2 pl-2 text-xs leading-relaxed italic">
              &ldquo;{evidence.sourceQuote}&rdquo;
            </blockquote>
          )}

          {/* Source document + open button */}
          {evidence.sourceDocument && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1">
                <FileText className="text-muted-foreground h-3 w-3 shrink-0" />
                <span className="text-muted-foreground truncate text-[10px]">
                  {evidence.sourceDocument.split("#")[0]}
                </span>
              </div>
              <Button
                className="h-6 shrink-0 px-2 text-[10px]"
                size="sm"
                variant="outline"
                onClick={() => onOpenSource(evidence)}
              >
                <Eye className="mr-1 h-3 w-3" />
                {t("tender.cvMatch.evidence.openCv")}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ── Requirement cell (with optional evidence popover) ─────────────────────────

type ReqCellProps = {
  matched: boolean
  overqualified: boolean
  evidence?: RequirementEvidence
  caseId: string
  cvLibraryId: string
  onOpenSource: (ev: RequirementEvidence) => void
}

/**
 * Cell for a requirement × candidate intersection.
 * When evidence is available, wraps the status icon in an EvidencePopover.
 * Falls back to a plain static icon for legacy results without evidence.
 */
function ReqCell({
  matched,
  overqualified,
  evidence,
  caseId,
  cvLibraryId,
  onOpenSource,
}: ReqCellProps) {
  if (evidence) {
    return (
      <EvidencePopover
        caseId={caseId}
        cvLibraryId={cvLibraryId}
        evidence={evidence}
        onOpenSource={onOpenSource}
      />
    )
  }
  return (
    <span className="mx-auto flex justify-center">
      <ReqStatusIcon matched={matched} overqualified={overqualified} />
    </span>
  )
}

// ── Requirement column header ─────────────────────────────────────────────────

/**
 * Column header for a single requirement.
 *
 * Hard requirements: default muted style.
 * Soft requirements: teal tint + "(mjukt)" label.
 * Meriting qualifications: blue tint + star prefix.
 */
function ReqColumnHeader({ req }: { req: ClassifiedRequirement & { isMeriting?: boolean } }) {
  const { t } = useTranslation()
  if (req.isMeriting) {
    return (
      <th className={cn("overflow-hidden text-center align-bottom", CELL_PADDING)} title={req.text}>
        <div className="flex flex-col items-center gap-1">
          <Star className="fill-accent text-accent h-3 w-3" />
          <span className="text-accent-foreground line-clamp-3 text-[10px] leading-snug break-words">
            {req.text}
          </span>
        </div>
      </th>
    )
  }
  if (req.type === "soft") {
    return (
      <th className={cn("overflow-hidden text-center align-bottom", CELL_PADDING)} title={req.text}>
        <div className="flex flex-col items-center gap-1">
          <span className="border-foreground bg-accent text-accent-foreground rounded-sm border px-1 py-0.5 text-[10px] font-medium">
            {t("tender.cvMatch.softRequirementBadge")}
          </span>
          <span className="text-muted-foreground line-clamp-3 text-[10px] leading-snug break-words">
            {req.text}
          </span>
        </div>
      </th>
    )
  }
  return (
    <th className={cn("overflow-hidden text-center align-bottom", CELL_PADDING)} title={req.text}>
      <span className="text-muted-foreground line-clamp-3 text-[10px] leading-snug break-words">
        {req.text}
      </span>
    </th>
  )
}

// ── Role pool — candidates as rows, requirements as columns ───────────────────

type RolePoolCardProps = {
  pool: RoleCandidatePool
  pickedForRole: LockedCandidate[]
  savedForRole: LockedCandidate[]
  caseId: string
  cvLibraryId: string
  /** When true, show empty table with column headers instead of "no candidates" message. */
  isPopulating?: boolean
  onAdd?: (candidate: CandidateMatch, roleId: string) => void
  onRemove?: (candidateId: string, roleId: string) => void
  onRemoveCandidate?: (candidateId: string) => void
  onOpenCv: (ev: RequirementEvidence) => void
}

function RolePoolCard({
  pool,
  pickedForRole,
  savedForRole,
  caseId,
  cvLibraryId,
  isPopulating = false,
  onAdd,
  onRemove,
  onRemoveCandidate,
  onOpenCv,
}: RolePoolCardProps) {
  const { t } = useTranslation()
  const { candidates, roleId, roleName, quantityRequired, classifiedRequirements } = pool

  const [open, setOpen] = useState(true)
  const [expandedCandidate, setExpandedCandidate] = useState<string | null>(null)

  const pickedIds = new Set(pickedForRole.map((lc) => lc.candidateId))
  const savedIds = new Set(savedForRole.map((lc) => lc.candidateId))
  const filledCount = pickedForRole.length

  let coverageColor = "text-destructive bg-destructive/15"
  if (filledCount >= quantityRequired) coverageColor = "text-success bg-success/20"
  else if (filledCount > 0) coverageColor = "text-warning bg-warning/20"

  // Build ordered column list: hard → soft → meriting.
  // Fall back to collecting all requirement strings from candidates when no
  // classification is available (e.g. legacy results before this feature).
  const hardCols: Array<ClassifiedRequirement & { isMeriting?: boolean }> = classifiedRequirements
    ? classifiedRequirements.hard
    : Array.from(
        new Set(candidates.flatMap((c) => [...c.matchedRequirements, ...c.missingRequirements]))
      ).map((text) => ({ text, type: "hard" as const, overqualifiedCounts: false }))

  const softCols: Array<ClassifiedRequirement & { isMeriting?: boolean }> = classifiedRequirements
    ? classifiedRequirements.soft
    : []

  const meritingCols: Array<ClassifiedRequirement & { isMeriting?: boolean }> = Array.from(
    new Set(candidates.flatMap((c) => c.meritingMatches))
  ).map((text) => ({ text, type: "hard" as const, overqualifiedCounts: false, isMeriting: true }))

  // Deduplicate by normalized text — the LLM may return the same requirement
  // in both hard and soft categories with slightly different formatting.
  const allColsRaw = [...hardCols, ...softCols, ...meritingCols]
  const seenColTexts = new Set<string>()
  const allCols = allColsRaw.filter((col) => {
    const norm = normalizeReqText(col.text)
    if (seenColTexts.has(norm)) return false
    seenColTexts.add(norm)
    return true
  })

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="bg-card overflow-hidden rounded-lg border">
        <CollapsibleTrigger asChild>
          <div className="hover:bg-accent/30 flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors">
            <Users className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 font-medium">{roleName}</span>
            <Badge className={cn("shrink-0", coverageColor)}>
              {t("tender.cvMatch.rolePool.coverageBadge", {
                filled: filledCount,
                required: quantityRequired,
              })}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {t("tender.cvMatch.rolePool.candidates", { count: candidates.length })}
            </span>
            {open ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {candidates.length === 0 && !isPopulating && (
            <p className="text-muted-foreground border-t py-4 text-center text-sm">
              {t("tender.cvMatch.rolePool.noMatches")}
            </p>
          )}
          {candidates.length === 0 && isPopulating ? (
            <div className="overflow-x-auto border-t">
              <table
                className="w-full table-fixed text-sm"
                style={{ minWidth: `${144 + allCols.length * COL_WIDTH_PX}px` }}
              >
                <colgroup>
                  <col style={{ width: "144px" }} />
                  {allCols.map((col) => (
                    <col key={col.text} style={{ width: `${COL_WIDTH_PX}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="bg-muted/40 border-b">
                    <th className="text-muted-foreground bg-muted sticky left-0 z-20 px-3 py-2 text-left text-xs font-medium">
                      {t("tender.cvMatch.rolePool.candidateColumn")}
                    </th>
                    {allCols.map((col) => (
                      <ReqColumnHeader key={col.text} req={col} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className="text-muted-foreground px-3 py-6 text-center text-xs"
                      colSpan={1 + allCols.length}
                    >
                      {t("tender.cvMatch.rolePool.addCvsHint")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto border-t">
              <table
                className="w-full table-fixed text-sm"
                style={{ minWidth: `${144 + allCols.length * COL_WIDTH_PX}px` }}
              >
                <colgroup>
                  <col style={{ width: "144px" }} />
                  {allCols.map((col) => (
                    <col key={col.text} style={{ width: `${COL_WIDTH_PX}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="bg-muted/40 border-b">
                    {/* Sticky candidate name column header — opaque bg-muted so scrolled content doesn't bleed through */}
                    <th
                      className={cn(
                        "text-muted-foreground bg-muted sticky left-0 z-20 text-left text-xs font-medium",
                        CELL_PADDING
                      )}
                    >
                      {t("tender.cvMatch.rolePool.candidateColumn")}
                    </th>
                    {allCols.map((col) => (
                      <ReqColumnHeader key={col.text} req={col} />
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {candidates.map((c) => {
                    const isPicked = pickedIds.has(c.candidateId)
                    const isSaved = savedIds.has(c.candidateId)
                    const matchedSet = buildNormalizedSet(c.matchedRequirements)
                    const overqualifiedSet = buildNormalizedSet(c.overqualifiedRequirements)
                    const meritSet = buildNormalizedSet(c.meritingMatches)
                    const isExpanded = expandedCandidate === c.candidateId
                    const hasDetails = !!(
                      c.notes ||
                      c.sourceSnippet ||
                      (c.requirementEvidence && c.requirementEvidence.length > 0)
                    )

                    // Build evidence lookup keyed by requirementId (primary) and text (fallback)
                    const evidenceLookup = buildEvidenceLookup(c.requirementEvidence)

                    const isPickedAndSaved = isPicked && isSaved
                    const isPickedDraft = isPicked && !isSaved

                    let stickyBg = "bg-card"
                    if (isPickedAndSaved) stickyBg = "bg-success/10"
                    else if (isPickedDraft) stickyBg = "bg-warning/10"

                    return (
                      <React.Fragment key={c.candidateId}>
                        <tr
                          className={cn(
                            "hover:bg-muted/10 border-b",
                            isPickedAndSaved && "bg-success/10",
                            isPickedDraft && "bg-warning/10"
                          )}
                        >
                          {/* Candidate name + controls */}
                          <td
                            className={cn(
                              "sticky left-0 z-10 align-middle",
                              CELL_PADDING,
                              stickyBg
                            )}
                          >
                            <div className="flex items-center gap-1.5">
                              {isPickedAndSaved && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="shrink-0">
                                      <Lock className="text-success h-3 w-3" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.titleLocked")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {isPickedDraft && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="shrink-0">
                                      <UserCheck className="text-warning h-3 w-3" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.titleDraftPick")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <Button
                                disabled={!hasDetails}
                                type="button"
                                variant="link"
                                className={cn(
                                  "h-auto min-w-0 flex-1 justify-start truncate p-0 text-left text-xs font-medium",
                                  isPickedAndSaved && "text-success",
                                  isPickedDraft && "text-warning",
                                  hasDetails && "hover:underline"
                                )}
                                title={
                                  hasDetails
                                    ? t("tender.cvMatch.rolePool.showCvDetails")
                                    : c.displayName
                                }
                                onClick={
                                  hasDetails
                                    ? () => setExpandedCandidate(isExpanded ? null : c.candidateId)
                                    : undefined
                                }
                              >
                                {c.displayName}
                              </Button>
                              {hasDetails && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      aria-label={t("tender.cvMatch.rolePool.showCvDetails")}
                                      size="icon"
                                      type="button"
                                      variant="ghost"
                                      className={cn(
                                        "size-7 shrink-0 p-0",
                                        isExpanded
                                          ? "text-primary"
                                          : "text-muted-foreground hover:text-foreground"
                                      )}
                                      onClick={() =>
                                        setExpandedCandidate(isExpanded ? null : c.candidateId)
                                      }
                                    >
                                      <ChevronDown
                                        className={cn(
                                          "h-3 w-3 transition-transform",
                                          isExpanded && "rotate-180"
                                        )}
                                      />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.showCvDetails")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {isPicked && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      aria-label={t("tender.cvMatch.rolePool.removeFromSelection")}
                                      className="text-muted-foreground hover:text-destructive size-7 shrink-0 p-0"
                                      size="icon"
                                      type="button"
                                      variant="ghost"
                                      onClick={() => onRemove?.(c.candidateId, roleId)}
                                    >
                                      <UserMinus className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.removeFromSelection")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {!isPicked && onAdd && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      aria-label={t("tender.cvMatch.rolePool.addToSelection")}
                                      className="text-muted-foreground hover:text-primary size-7 shrink-0 p-0"
                                      size="icon"
                                      type="button"
                                      variant="ghost"
                                      onClick={() => onAdd(c, roleId)}
                                    >
                                      <UserPlus className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.addToSelection")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {onRemoveCandidate && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      aria-label={t("tender.cvMatch.rolePool.removeCandidateTitle")}
                                      className="text-muted-foreground/50 hover:text-destructive size-7 shrink-0 p-0"
                                      size="icon"
                                      type="button"
                                      variant="ghost"
                                      onClick={() => onRemoveCandidate(c.candidateId)}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t("tender.cvMatch.rolePool.removeCandidateTitle")}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </td>

                          {/* Requirement cells — one per column */}
                          {allCols.map((col) => {
                            const normText = normalizeReqText(col.text)
                            if (col.isMeriting) {
                              const hasMerit = meritSet.has(normText)
                              return (
                                <td
                                  key={col.text}
                                  className={cn("text-center align-middle", CELL_PADDING)}
                                >
                                  {hasMerit ? (
                                    <Star className="fill-accent text-accent mx-auto h-3.5 w-3.5" />
                                  ) : (
                                    <span className="text-muted-foreground/30">–</span>
                                  )}
                                </td>
                              )
                            }
                            const evidence = lookupEvidence(
                              evidenceLookup,
                              col.requirementId,
                              normText
                            )
                            const matched = evidence
                              ? evidence.status !== "missing"
                              : matchedSet.has(normText)
                            const overqualified = evidence
                              ? evidence.status === "overqualified"
                              : matched && overqualifiedSet.has(normText)
                            return (
                              <td
                                key={col.text}
                                className={cn("text-center align-middle", CELL_PADDING)}
                              >
                                <ReqCell
                                  caseId={caseId}
                                  cvLibraryId={cvLibraryId}
                                  evidence={evidence}
                                  matched={matched}
                                  overqualified={overqualified}
                                  onOpenSource={onOpenCv}
                                />
                              </td>
                            )
                          })}
                        </tr>

                        {/* Restyled candidate detail panel */}
                        {isExpanded && hasDetails && (
                          <tr key={`${c.candidateId}-detail`}>
                            <td className="bg-muted/10 border-b" colSpan={1 + allCols.length}>
                              <div className="sticky left-0 w-fit max-w-[min(48rem,calc(100vw-4rem))] px-4 py-3">
                                <div className="space-y-3">
                                  {/* Header with optional Open CV button */}
                                  {(() => {
                                    // Find the primary source document for this candidate —
                                    // first matched evidence entry with a source.
                                    const primaryEvidence = c.requirementEvidence?.find(
                                      (ev) => ev.sourceDocument && ev.status !== "missing"
                                    )
                                    return (
                                      <div className="flex items-center justify-between gap-2">
                                        <p className="text-foreground text-xs font-semibold">
                                          {t("tender.cvMatch.rolePool.detailHeading", {
                                            name: c.displayName,
                                          })}
                                        </p>
                                        {primaryEvidence && (
                                          <Button
                                            className="h-6 shrink-0 px-2 text-[10px]"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => onOpenCv(primaryEvidence)}
                                          >
                                            <Eye className="mr-1 h-3 w-3" />
                                            {t("tender.cvMatch.evidence.openCv")}
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  })()}

                                  {/* AI notes */}
                                  {c.notes && (
                                    <p className="text-muted-foreground text-xs leading-relaxed">
                                      {c.notes}
                                    </p>
                                  )}

                                  {/* Per-requirement evidence summary */}
                                  {c.requirementEvidence && c.requirementEvidence.length > 0 && (
                                    <div className="space-y-1.5">
                                      <p className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                                        {t("tender.cvMatch.rolePool.requirementCheckHeading")}
                                      </p>
                                      <div className="grid gap-1.5 sm:grid-cols-2">
                                        {c.requirementEvidence.map((ev) => {
                                          let evBorderClass = "border-success/30 bg-success/10"
                                          if (ev.status === "missing")
                                            evBorderClass =
                                              "border-destructive/30 bg-destructive/15"
                                          else if (ev.status === "overqualified")
                                            evBorderClass = "border-warning/30 bg-warning/10"
                                          return (
                                            <div
                                              key={ev.requirementText}
                                              className={cn(
                                                "rounded-md border px-2.5 py-2 text-xs",
                                                evBorderClass
                                              )}
                                            >
                                              <div className="mb-1 flex items-start gap-2">
                                                <span className="mt-0.5 shrink-0">
                                                  <ReqStatusIcon
                                                    matched={ev.status !== "missing"}
                                                    overqualified={ev.status === "overqualified"}
                                                  />
                                                </span>
                                                <span className="text-foreground/80 leading-snug font-medium">
                                                  {ev.requirementText}
                                                </span>
                                              </div>
                                              {ev.reasoning && (
                                                <p className="text-muted-foreground mb-1 pl-6 leading-relaxed">
                                                  {ev.reasoning}
                                                </p>
                                              )}
                                              {ev.sourceQuote && (
                                                <blockquote className="border-muted-foreground/20 text-muted-foreground border-l-2 pl-2 leading-snug italic">
                                                  &ldquo;{ev.sourceQuote}&rdquo;
                                                </blockquote>
                                              )}
                                              {ev.sourceDocument && (
                                                <Button
                                                  className="text-muted-foreground hover:text-foreground mt-1.5 h-auto gap-1 p-0 pl-6 text-[10px] font-normal"
                                                  type="button"
                                                  variant="link"
                                                  onClick={() => onOpenCv(ev)}
                                                >
                                                  <Eye className="h-3 w-3" />
                                                  {ev.sourceDocument.split("#")[0]}
                                                </Button>
                                              )}
                                            </div>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Legacy: fallback source snippet when no structured evidence */}
                                  {!c.requirementEvidence?.length && c.sourceSnippet && (
                                    <blockquote className="border-muted-foreground/30 text-muted-foreground border-l-2 pl-3 text-xs leading-relaxed italic">
                                      &ldquo;{c.sourceSnippet}&rdquo;
                                    </blockquote>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ── Staffing plan ─────────────────────────────────────────────────────────────

type StaffingPlanProps = {
  pickedCandidates: LockedCandidate[]
  savedCandidates: LockedCandidate[]
  isDirty: boolean
  isSaving: boolean
  roles: Array<Pick<RoleRequirement, "id" | "quantity" | "roleName">>
  onRemove?: (candidateId: string, roleId: string) => void
  onSave?: () => void
  onReset?: () => void
}

function StaffingPlan({
  pickedCandidates,
  savedCandidates,
  isDirty,
  isSaving,
  roles,
  onRemove,
  onSave,
  onReset,
}: StaffingPlanProps) {
  const { t } = useTranslation()
  const totalRequired = roles.reduce((sum, r) => sum + r.quantity, 0)
  const totalFilled = pickedCandidates.length
  const allFilled = totalFilled >= totalRequired

  let statusColor = "text-muted-foreground bg-muted/30"
  if (allFilled) statusColor = "text-success bg-success/10 border-success/30"
  else if (totalFilled > 0) statusColor = "text-warning bg-warning/10 border-warning/30"

  const savedIds = new Set(savedCandidates.map((lc) => `${lc.roleId}:${lc.candidateId}`))

  return (
    <Card className="sticky top-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCheck className="h-4 w-4" />
          {t("tender.cvMatch.staffingPlan.title")}
          {isSaving && <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />}
        </CardTitle>
        <div className={cn("rounded-md border px-3 py-2 text-sm", statusColor)}>
          {allFilled
            ? t("tender.cvMatch.staffingPlan.allRolesFilled")
            : t("tender.cvMatch.staffingPlan.rolesFilledProgress", {
                filled: totalFilled,
                required: totalRequired,
              })}
        </div>
        {isDirty && (
          <p className="text-muted-foreground text-xs">
            {t("tender.cvMatch.staffingPlan.dirtyHint")}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {roles.map((role) => {
          const pickedForRole = pickedCandidates.filter((lc) => lc.roleId === role.id)
          const slotsLeft = role.quantity - pickedForRole.length

          let roleAccent = "border-l-destructive"
          if (pickedForRole.length >= role.quantity) roleAccent = "border-l-success"
          else if (pickedForRole.length > 0) roleAccent = "border-l-warning"

          return (
            <div key={role.id} className={cn("border-l-4 pl-3", roleAccent)}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="truncate text-sm font-medium">{role.roleName}</span>
                <Badge className="ml-2 shrink-0" variant="outline">
                  {pickedForRole.length}/{role.quantity}
                </Badge>
              </div>

              {pickedForRole.map((lc) => {
                const isSaved = savedIds.has(`${lc.roleId}:${lc.candidateId}`)
                return (
                  <div
                    key={lc.candidateId}
                    className={cn(
                      "mb-1 flex items-center justify-between rounded-md border px-3 py-1.5",
                      isSaved
                        ? "border-success/30 bg-success/10"
                        : "border-warning/50 bg-warning/10"
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {isSaved ? (
                        <Lock className="text-success h-3 w-3 shrink-0" />
                      ) : (
                        <UserPlus className="text-warning h-3 w-3 shrink-0" />
                      )}
                      <span className="truncate text-sm">{lc.displayName}</span>
                    </div>
                    {onRemove && (
                      <Button
                        className="ml-2 h-6 w-6 shrink-0"
                        size="icon"
                        variant="ghost"
                        onClick={() => onRemove(lc.candidateId, lc.roleId)}
                      >
                        <UserMinus className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )
              })}

              {Array.from({ length: Math.max(0, slotsLeft) }).map((_, i) => (
                <div
                  key={i}
                  className="border-muted mb-1 flex items-center gap-2 rounded-md border border-dashed px-3 py-1.5"
                >
                  <UserPlus className="text-muted-foreground h-3 w-3" />
                  <span className="text-muted-foreground text-sm">
                    {t("tender.cvMatch.staffingPlan.openSlot")}
                  </span>
                </div>
              ))}
              <Separator className="mt-3" />
            </div>
          )
        })}

        <div className="flex flex-col gap-2 pt-1">
          {onSave && (
            <Button className="w-full" disabled={!isDirty || isSaving} onClick={onSave}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("tender.cvMatch.staffingPlan.saving")}
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  {t("tender.cvMatch.staffingPlan.lockPlan")}
                </>
              )}
            </Button>
          )}
          {onReset && isDirty && (
            <Button className="w-full" disabled={isSaving} variant="ghost" onClick={onReset}>
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              {t("tender.cvMatch.staffingPlan.reset")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ── Classify panel (Sub-step A) ───────────────────────────────────────────────

type ClassifyPanelProps = {
  extractionCompleted: boolean
  isClassifying: boolean
  roleRequirements: RoleRequirement[]
  /** When true, a "Återställ" button is shown to go back to State 1. */
  canReset?: boolean
  canEdit?: boolean
  onClassify: () => void
  onReset?: () => void
}

function ClassifyPanel({
  extractionCompleted,
  isClassifying,
  roleRequirements,
  canReset = false,
  canEdit = true,
  onClassify,
  onReset,
}: ClassifyPanelProps) {
  const { t } = useTranslation()
  const canClassify =
    canEdit && extractionCompleted && roleRequirements.length > 0 && !isClassifying

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-6 py-10">
        <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-full">
          <Users className="text-muted-foreground h-8 w-8" />
        </div>
        <div className="max-w-sm space-y-1 text-center">
          <h3 className="font-semibold">{t("tender.cvMatch.classify.title")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("tender.cvMatch.classify.description")}
          </p>
        </div>

        {!extractionCompleted && (
          <div className="bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-md px-4 py-2 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t("tender.cvMatch.classify.extractionRequired")}
          </div>
        )}

        {extractionCompleted && roleRequirements.length === 0 && (
          <div className="bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-md px-4 py-2 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t("tender.cvMatch.classify.noRolesFound")}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button disabled={!canClassify} onClick={onClassify}>
            {isClassifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tender.cvMatch.classify.analyzing")}
              </>
            ) : (
              t("tender.cvMatch.classify.analyzeButton")
            )}
          </Button>
          {canReset && onReset && (
            <Button disabled={isClassifying} variant="ghost" onClick={onReset}>
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              {t("tender.cvMatch.classify.reset")}
            </Button>
          )}
        </div>

        <TenderAiDisclaimer />
      </CardContent>
    </Card>
  )
}

// ── CV source label helper ────────────────────────────────────────────────────

const CV_SOURCE_LABEL_KEYS: Record<string, string> = {
  auto_match: "tender.cvMatch.sourceLabels.auto_match",
  manual_search: "tender.cvMatch.sourceLabels.manual_search",
  extraction: "tender.cvMatch.sourceLabels.extraction",
  upload: "tender.cvMatch.sourceLabels.upload",
}

function cvSourceLabel(t: TFunction, source: string | undefined): string {
  const key = CV_SOURCE_LABEL_KEYS[source ?? ""]
  return key ? t(key) : t("tender.cvMatch.sourceLabels.fallback")
}

// ── Unified CV list entry ─────────────────────────────────────────────────────

type CvListEntry = {
  /** Present when the CV has a registered document (uploadable/deletable). Absent for auto-match library candidates. */
  docId?: string
  filename: string
  sizeKb?: number
  status: "analysed" | "pending" | "ready" | "processing" | "failed"
  source: string
}

function buildCvListEntries(
  cvDocuments: TenderDocument[],
  rolePools: RoleCandidatePool[],
  analysingDocs: Set<string>
): CvListEntry[] {
  // Build filename → cvSource from candidate pools (covers auto_match + manual_search + upload)
  const sourceByFilename = new Map<string, string>()
  for (const pool of rolePools) {
    for (const c of pool.candidates) {
      const filename = c.candidateId.split("#")[0]
      if (!sourceByFilename.has(filename)) {
        sourceByFilename.set(filename, c.cvSource ?? "")
      }
    }
  }

  const entries: CvListEntry[] = []
  const seenFilenames = new Set<string>()

  // First: all cvDocuments (have docId — can be deleted, can show "Analysera" button)
  for (const doc of cvDocuments) {
    seenFilenames.add(doc.filename)
    const isPending = analysingDocs.has(doc.filename)
    const isAnalysed = sourceByFilename.has(doc.filename)
    const isProcessing = doc.status === "uppladdad" || doc.status === "indexeras"
    const isFailed = doc.status === "fel"

    let status: CvListEntry["status"]
    if (isPending) status = "pending"
    else if (isAnalysed) status = "analysed"
    else if (isFailed) status = "failed"
    else if (isProcessing) status = "processing"
    else status = "ready"

    entries.push({
      docId: doc.id,
      filename: doc.filename,
      sizeKb: doc.sizeKb,
      status,
      source: sourceByFilename.get(doc.filename) ?? "",
    })
  }

  // Second: candidates from role pools not covered by a cvDocument
  // (auto_match results that were never registered as case documents)
  for (const [filename, source] of sourceByFilename) {
    if (seenFilenames.has(filename)) continue
    const isPending = analysingDocs.has(filename)
    entries.push({
      filename,
      status: isPending ? "pending" : "analysed",
      source,
    })
  }

  return entries
}

// ── CV source panel ────────────────────────────────────────────────────────────

type CvSourcePanelProps = {
  caseId: string
  runId: string
  cvLibraryId: string
  /** Unified list of all CV entries for this run. */
  cvListEntries: CvListEntry[]
  isAutoMatching: boolean
  onPreviewCv: (libraryId: string, sourcepage: string, sourcefile: string) => void
  onAutoMatch: (libraryId: string) => void
  /** Called when a doc starts being analysed (from attach flow). */
  onDocAnalysisStart: (filename: string) => void
  /** Called when a doc analysis finishes or errors. */
  onDocAnalysisEnd: (filename: string) => void
  onUploadCvDocument: (files: File[]) => void
  onRemoveCvDocument: (docId: string) => void
  isUploadingCv: boolean
  /** Keyword used to auto-detect the CV library (defaults to "cv"). */
  cvLibraryKeyword?: string
  canEdit?: boolean
}

function CvSourcePanel({
  caseId,
  runId,
  cvLibraryId,
  cvListEntries,
  isAutoMatching,
  onPreviewCv,
  onAutoMatch,
  onDocAnalysisStart,
  onDocAnalysisEnd,
  onUploadCvDocument,
  onRemoveCvDocument,
  isUploadingCv,
  cvLibraryKeyword = "cv",
  canEdit = true,
}: CvSourcePanelProps) {
  const { t } = useTranslation()
  const { data: config } = useConfigQuery()
  const libraries = config?.indexes ?? []

  const [query, setQuery] = useState("")
  const autoLibrary = detectCvLibrary(libraries, cvLibraryKeyword)
  const [selectedLibraryId, setSelectedLibraryId] = useState(
    autoLibrary?.id || cvLibraryId || libraries[0]?.id || ""
  )

  const cvSearch = useCvLibrarySearch(caseId)
  const attachCv = useAttachCvFromLibrary(caseId)
  const analyseDoc = useAnalyseDocForCvMatch(caseId)

  // Sync selectedLibraryId once libraries load (config is async).
  // Keyword-detected library always takes priority; fall back to stored
  // cvLibraryId or the first available library.
  useEffect(() => {
    if (libraries.length === 0) return
    const matched = detectCvLibrary(libraries, cvLibraryKeyword)
    if (matched) {
      setSelectedLibraryId(matched.id)
    } else if (!selectedLibraryId) {
      setSelectedLibraryId(cvLibraryId || libraries[0]?.id || "")
    }

    // resetting user's manual dropdown selection on every render
  }, [libraries, cvLibraryKeyword])

  const [pickerOpen, setPickerOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleGraphFileSelect = async (item: GraphFileItem) => {
    const token = SettingsService.getInstance().token
    const blob = await downloadGraphFileApi(item, token)
    const mime = item.mimeType || blob.type || "application/octet-stream"
    onUploadCvDocument([new File([blob], item.name, { type: mime })])
  }

  const handleDocumentsSelected = (documents: UserDocument[]) => {
    const files = documents.map((doc) => {
      const extension = doc.contentFormat === "markdown" ? ".md" : ".txt"
      const safeName = (doc.title || "untitled-document").replace(/[/\\?%*:|"<>]/g, "-")
      return new File([doc.content], `${safeName}${extension}`, { type: "text/plain" })
    })
    onUploadCvDocument(files)
  }

  const handleSearch = () => {
    if (!query.trim() || !selectedLibraryId) return
    cvSearch.mutate({ cvLibraryId: selectedLibraryId, query: query.trim() })
  }

  const handleAttach = (result: CvSearchResult) => {
    attachCv.mutate(
      { runId, cvLibraryId: result.libraryId, sourcefile: result.sourcefile },
      {
        onSuccess: (attachResult) => {
          const docId = attachResult.document.id
          const filename = result.sourcefile
          onDocAnalysisStart(filename)
          analyseDoc.mutate(
            { runId, docId, cvLibraryId: selectedLibraryId, cvSource: "manual_search" },
            { onSettled: () => onDocAnalysisEnd(filename) }
          )
        },
      }
    )
  }

  const knownFilenames = new Set(cvListEntries.map((e) => e.filename))
  const hasPending = cvListEntries.some((e) => e.status === "pending")

  const firstChunkResults = (cvSearch.data ?? []).filter((r) => r.isFirstChunkForFile)

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">{t("tender.cvMatch.findCandidatesTitle")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("tender.cvMatch.findCandidatesDescription")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {autoLibrary ? (
            <div className="bg-muted/40 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm">
              <span className="text-muted-foreground text-xs">
                {t("tender.cvMatch.cvList.libraryLabel")}
              </span>
              <span className="font-medium">{autoLibrary.display_name}</span>
            </div>
          ) : (
            <Select
              disabled={libraries.length === 0}
              value={selectedLibraryId}
              onValueChange={setSelectedLibraryId}
            >
              <SelectTrigger className="w-40 text-sm">
                <SelectValue placeholder={t("tender.cvMatch.cvList.libraryPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {libraries.map((lib) => (
                  <SelectItem key={lib.id} value={lib.id}>
                    {lib.display_name || lib.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            disabled={!canEdit || !selectedLibraryId || isAutoMatching}
            variant="outline"
            onClick={() => onAutoMatch(selectedLibraryId)}
          >
            {isAutoMatching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tender.cvMatch.cvList.searchingShort")}
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                {t("tender.cvMatch.cvList.runAutoMatch")}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ── Unified CV list ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4" />
              {t("tender.cvMatch.cvList.analysedTitle")}
              {(hasPending || isAutoMatching) && (
                <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {cvListEntries.length === 0 && !isAutoMatching ? (
              <p className="text-muted-foreground py-2 text-center text-xs">
                {t("tender.cvMatch.cvList.emptyHint")}
              </p>
            ) : (
              <div className="space-y-1">
                {cvListEntries.map((entry) => {
                  const isAnalysed = entry.status === "analysed"
                  const isPending = entry.status === "pending"
                  const isReady = entry.status === "ready"
                  const isProcessing = entry.status === "processing"
                  const isFailed = entry.status === "failed"

                  let iconClass = "text-muted-foreground"
                  if (isFailed) iconClass = "text-destructive"
                  else if (isAnalysed) iconClass = "text-success"

                  return (
                    <div
                      key={entry.filename}
                      className={cn(
                        "border-border flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs",
                        isFailed && "border-destructive",
                        (isPending || isProcessing) && "border-border/50 bg-muted/20"
                      )}
                    >
                      {isPending || isProcessing ? (
                        <Loader2 className="text-muted-foreground h-3.5 w-3.5 shrink-0 animate-spin" />
                      ) : (
                        <FileText className={cn("h-3.5 w-3.5 shrink-0", iconClass)} />
                      )}
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate font-medium",
                          (isPending || isProcessing) && "text-muted-foreground font-normal"
                        )}
                      >
                        {entry.filename.replace(/\.[^.]+$/, "")}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {isProcessing && (
                          <span className="text-muted-foreground/60 text-[10px]">
                            {t("tender.cvMatch.cvList.processing")}
                          </span>
                        )}
                        {isPending && (
                          <span className="text-muted-foreground/60 text-[10px]">
                            {t("tender.cvMatch.cvList.analysing")}
                          </span>
                        )}
                        {isFailed && (
                          <span className="text-destructive text-[10px]">
                            {t("tender.cvMatch.cvList.errorShort")}
                          </span>
                        )}
                        {isAnalysed && (
                          <>
                            <span className="text-muted-foreground bg-muted/60 shrink-0 rounded px-1.5 py-0.5 text-[10px]">
                              {cvSourceLabel(t, entry.source)}
                            </span>
                            <Check className="text-success h-3 w-3" />
                          </>
                        )}
                        {isReady && entry.docId && (
                          <Button
                            className="h-6 px-2 text-[10px]"
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              onDocAnalysisStart(entry.filename)
                              analyseDoc.mutate(
                                {
                                  runId,
                                  docId: entry.docId!,
                                  cvLibraryId: selectedLibraryId,
                                  cvSource: "upload",
                                },
                                { onSettled: () => onDocAnalysisEnd(entry.filename) }
                              )
                            }}
                          >
                            <Search className="mr-1 h-3 w-3" />
                            {t("tender.cvMatch.cvList.analyseButton")}
                          </Button>
                        )}
                        {canEdit && entry.docId && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Radix tooltip-on-disabled-control wrapper must stay focusable */}
                              <span tabIndex={isPending || isProcessing || isUploadingCv ? 0 : -1}>
                                <Button
                                  aria-label={t("tender.cvMatch.cvList.removeTitle")}
                                  className="h-6 w-6 p-0"
                                  disabled={isPending || isProcessing || isUploadingCv}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => onRemoveCvDocument(entry.docId!)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("tender.cvMatch.cvList.removeTitle")}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  )
                })}
                {/* Auto-match placeholder row when no entries yet */}
                {isAutoMatching && cvListEntries.length === 0 && (
                  <div className="border-border/50 bg-muted/20 flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs">
                    <Loader2 className="text-muted-foreground h-3.5 w-3.5 shrink-0 animate-spin" />
                    <span className="text-muted-foreground">
                      {t("tender.cvMatch.searchingLibrary")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Upload drop zone ── */}
            <Button
              disabled={isUploadingCv}
              type="button"
              variant="outline"
              className={cn(
                "border-border flex h-auto w-full cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 font-normal",
                !canEdit && "pointer-events-none opacity-50",
                canEdit && isUploadingCv && "pointer-events-none opacity-60",
                canEdit && !isUploadingCv && "hover:border-foreground/30 hover:bg-muted/30",
                isDragOver && canEdit && !isUploadingCv && "border-primary bg-primary/5"
              )}
              onClick={() => setPickerOpen(true)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={(e) => {
                e.preventDefault()
                if (!isUploadingCv) setIsDragOver(true)
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragOver(false)
                if (isUploadingCv) return
                const { allowed } = filterAllowedFiles(Array.from(e.dataTransfer.files))
                if (allowed.length > 0) onUploadCvDocument(allowed)
              }}
            >
              {isUploadingCv ? (
                <Loader2 className="text-muted-foreground h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Upload className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
              <div>
                <span className="text-muted-foreground text-xs">
                  {isUploadingCv
                    ? t("tender.cvMatch.cvList.uploading")
                    : t("tender.cvMatch.cvList.uploadCvFiles")}
                </span>
                <span className="text-muted-foreground/60 ml-2 text-[10px]">
                  {t("tender.cvMatch.cvList.fileTypesHint")}
                </span>
              </div>
            </Button>
            <FilePickerDialog
              accept={TENDER_ACCEPT_FILTER}
              open={pickerOpen}
              source="recent"
              onDocumentsSelected={handleDocumentsSelected}
              onLocalFilesSelected={onUploadCvDocument}
              onOpenChange={setPickerOpen}
              onSelect={handleGraphFileSelect}
            />
          </CardContent>
        </Card>

        {/* ── Library search ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Search className="h-4 w-4" />
              {t("tender.cvMatch.librarySearch.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-2">
              <Input
                className="flex-1 text-sm"
                placeholder={t("tender.cvMatch.librarySearch.placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button
                disabled={!query.trim() || !selectedLibraryId || cvSearch.isPending}
                size="sm"
                onClick={handleSearch}
              >
                {cvSearch.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Search className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>

            {firstChunkResults.length > 0 && (
              <div className="space-y-1">
                {firstChunkResults.map((result) => {
                  const isAttached = knownFilenames.has(result.sourcefile)
                  const attachingSourcefile = attachCv.isPending
                    ? (attachCv.variables as { sourcefile: string } | undefined)?.sourcefile
                    : undefined
                  const isAttaching =
                    attachingSourcefile === result.sourcefile && attachCv.isPending
                  const isAnalysingAttached =
                    analyseDoc.isPending &&
                    (analyseDoc.variables as { docId: string } | undefined)?.docId ===
                      result.sourcefile

                  return (
                    <div
                      key={result.sourcefile}
                      className={cn(
                        "border-border flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs",
                        isAttached && "border-success/30 bg-success/10"
                      )}
                    >
                      <FileText
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          isAttached ? "text-success" : "text-muted-foreground"
                        )}
                      />
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate font-medium",
                          isAttached && "text-success"
                        )}
                      >
                        {result.sourcefile.replace(/\.[^.]+$/, "")}
                      </span>
                      <div className="flex shrink-0 gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              aria-label={t("tender.cvMatch.librarySearch.previewTitle")}
                              className="h-6 px-2"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                onPreviewCv(result.libraryId, result.sourcepage, result.sourcefile)
                              }
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t("tender.cvMatch.librarySearch.previewTitle")}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Radix tooltip-on-disabled-control wrapper must stay focusable
                              tabIndex={isAttached || isAttaching || isAnalysingAttached ? 0 : -1}
                            >
                              <Button
                                className="h-6 px-2"
                                disabled={isAttached || isAttaching || isAnalysingAttached}
                                size="sm"
                                variant={isAttached ? "outline" : "secondary"}
                                aria-label={
                                  isAttached
                                    ? t("tender.cvMatch.librarySearch.alreadyAnalysedTitle")
                                    : t("tender.cvMatch.librarySearch.attachTitle")
                                }
                                onClick={() => handleAttach(result)}
                              >
                                {(isAttaching || isAnalysingAttached) && (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                )}
                                {!isAttaching && !isAnalysingAttached && isAttached && (
                                  <Check className="text-success h-3 w-3" />
                                )}
                                {!isAttaching && !isAnalysingAttached && !isAttached && (
                                  <Paperclip className="h-3 w-3" />
                                )}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isAttached
                              ? t("tender.cvMatch.librarySearch.alreadyAnalysedTitle")
                              : t("tender.cvMatch.librarySearch.attachTitle")}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {cvSearch.isSuccess && firstChunkResults.length === 0 && (
              <p className="text-muted-foreground py-2 text-center text-xs">
                {t("tender.cvMatch.librarySearch.noResultsForQuery", { query })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ── Error state ───────────────────────────────────────────────────────────────

function ErrorPanel({ error, onRetry }: { error?: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <Card className="border-destructive">
      <CardContent className="flex flex-col items-center gap-4 py-10">
        <AlertCircle className="text-destructive h-10 w-10" />
        <div className="space-y-1 text-center">
          <h3 className="text-destructive font-semibold">{t("tender.cvMatch.errorPanel.title")}</h3>
          {error && <p className="text-muted-foreground max-w-sm text-sm">{error}</p>}
        </div>
        <Button variant="outline" onClick={onRetry}>
          {t("tender.cvMatch.errorPanel.tryAgain")}
        </Button>
      </CardContent>
    </Card>
  )
}

// ── Main section ──────────────────────────────────────────────────────────────

type CvMatchingSectionProps = {
  caseId: string
  caseTitle: string
  extractionCompleted: boolean
  roleRequirements: RoleRequirement[]
  runId: string
  cvDocuments?: TenderDocument[]
  /** "classified" | "completed" | "pending" | null — from run.cvMatchStatus */
  cvMatchStatus?: string | null
  onUploadCvDocument: (files: File[]) => void
  onRemoveCvDocument: (docId: string) => void
  isUploadingCv: boolean
  /** Keyword used to auto-detect the CV library (defaults to "cv"). */
  cvLibraryKeyword?: string
  canEdit?: boolean
}

export function CvMatchingSection({
  caseId,
  caseTitle,
  extractionCompleted,
  roleRequirements,
  runId,
  cvDocuments = [],
  cvMatchStatus,
  onUploadCvDocument,
  onRemoveCvDocument,
  isUploadingCv,
  cvLibraryKeyword = "cv",
  canEdit = true,
}: CvMatchingSectionProps) {
  const { t, i18n } = useTranslation()
  const startCvMatch = useStartCvMatch(caseId)
  const abortCvMatch = useAbortCvMatch(caseId)
  const classifyCv = useClassifyCvRequirements(caseId)
  const resetCvMatch = useResetCvMatch(caseId)
  const updateLocked = useUpdateLockedCandidates(caseId)
  const analyseDoc = useAnalyseDocForCvMatch(caseId)
  const removeCandidate = useRemoveCandidate(caseId)

  // When true, suppress the cached result and disable the query so the trigger
  // panel is shown even though old data exists on the server.
  const [isRerunMode, setIsRerunMode] = useState(false)
  const [showRerunConfirm, setShowRerunConfirm] = useState(false)
  const [showAbortConfirm, setShowAbortConfirm] = useState(false)

  // Filenames currently being analysed (pending → spinner in CV list)
  const [analysingDocs, setAnalysingDocs] = useState<Set<string>>(new Set())

  const addAnalysing = (filename: string) =>
    setAnalysingDocs((prev) => new Set([...prev, filename]))
  const removeAnalysing = (filename: string) =>
    setAnalysingDocs((prev) => {
      const next = new Set(prev)
      next.delete(filename)
      return next
    })

  // A result document does not exist until CV matching has been started. Do
  // not turn that normal initial state into a noisy 404 in the browser.
  const { data: polledData } = useCvMatchResult(caseId, runId, {
    enabled: !isRerunMode && cvMatchStatus != null,
  })
  // polledData is { result, status } — result is null for pending/reset docs.
  const polledResult = polledData?.result ?? undefined
  const polledStatus = polledData?.status ?? undefined

  const cvMatch: CvMatchResult | undefined = isRerunMode ? undefined : polledResult

  // True while the background pipeline is running — polling delivers progressive results
  const isProcessing = !isRerunMode && (polledStatus === "processing" || startCvMatch.isPending)
  const isAbortPending = abortCvMatch.isPending || cvMatch?.cancelRequested === true
  const failureMessage = getPipelineFailureMessage(t, cvMatch?.failureReason, cvMatch?.error)

  // ── Auto-analyse attached CV documents after classification ──────────────────
  // When classification succeeds and the run already has cv_document_ids, fire
  // analyseDoc for each ready doc. Done in the frontend so it's decoupled from
  // the classify network call and not fragile as a background server task.
  const cvDocumentsRef = React.useRef(cvDocuments)
  cvDocumentsRef.current = cvDocuments
  useEffect(() => {
    if (!classifyCv.isSuccess) return
    const readyDocs = cvDocumentsRef.current.filter((d) => d.status === "indexerad")
    if (readyDocs.length === 0) return

    const MAX_CONCURRENT_ANALYSE = 2

    const analyseOne = (doc: (typeof readyDocs)[number]) =>
      new Promise<void>((resolve) => {
        addAnalysing(doc.filename)
        analyseDoc.mutate(
          { runId, docId: doc.id, cvLibraryId: "", cvSource: "extraction" },
          {
            onSettled: () => {
              removeAnalysing(doc.filename)
              resolve()
            },
          }
        )
      })

    void (async () => {
      for (let i = 0; i < readyDocs.length; i += MAX_CONCURRENT_ANALYSE) {
        const batch = readyDocs.slice(i, i + MAX_CONCURRENT_ANALYSE)
        await Promise.all(batch.map((doc) => analyseOne(doc)))
      }
    })()
  }, [classifyCv.isSuccess]) // intentionally only runs when isSuccess flips to true

  // ── Auto-analyse uploaded CV documents once they finish indexing ──────────────
  // Track IDs seen on the previous render to detect newly appearing doc IDs
  // (i.e. docs that were just uploaded this session). Only fire analysis once
  // per doc and only when cvMatch exists (table is ready).
  const prevCvDocIdsRef = React.useRef<Set<string>>(new Set(cvDocuments.map((d) => d.id)))
  const firedDocIds = React.useRef<Set<string>>(new Set())
  const [pendingUploadIds, setPendingUploadIds] = React.useState<Set<string>>(new Set())

  useEffect(() => {
    const currentIds = new Set(cvDocuments.map((d) => d.id))
    const newIds = cvDocuments.filter((d) => !prevCvDocIdsRef.current.has(d.id)).map((d) => d.id)
    prevCvDocIdsRef.current = currentIds

    if (newIds.length > 0) {
      setPendingUploadIds((prev) => new Set([...prev, ...newIds]))
    }
  }, [cvDocuments])

  // Watch cvDocuments: when a pending upload becomes "indexerad", auto-fire analysis.
  useEffect(() => {
    if (!cvMatch || pendingUploadIds.size === 0) return
    for (const doc of cvDocuments) {
      if (
        doc.status === "indexerad" &&
        pendingUploadIds.has(doc.id) &&
        !firedDocIds.current.has(doc.id) &&
        !analysingDocs.has(doc.filename)
      ) {
        firedDocIds.current.add(doc.id)
        addAnalysing(doc.filename)
        analyseDoc.mutate(
          { runId, docId: doc.id, cvLibraryId: "", cvSource: "upload" },
          { onSettled: () => removeAnalysing(doc.filename) }
        )
      }
    }
  }, [cvDocuments, cvMatch, pendingUploadIds, analysingDocs])

  // ── Saved state — what is currently persisted in Cosmos ──
  const savedLockedCandidates = useMemo(
    () => (cvMatch?.lockedCandidates ?? []).filter((lc) => !!lc.candidateId && !!lc.roleId),
    [cvMatch?.lockedCandidates]
  )

  // ── Draft state — local picks that haven't been saved yet ──
  const [draftPickedCandidates, setDraftPickedCandidates] =
    useState<LockedCandidate[]>(savedLockedCandidates)

  // Reset the draft when the persisted data changes (e.g. after a successful save
  // or when the user changes runs).
  const savedKey = useMemo(
    () =>
      savedLockedCandidates
        .map((lc) => `${lc.roleId}:${lc.candidateId}`)
        .sort()
        .join("|"),
    [savedLockedCandidates]
  )
  // savedLockedCandidates is intentionally omitted — savedKey already captures identity changes
  useEffect(() => {
    setDraftPickedCandidates(savedLockedCandidates)
  }, [savedKey])

  const isDirty = useMemo(() => {
    if (draftPickedCandidates.length !== savedLockedCandidates.length) return true
    const savedSet = new Set(savedLockedCandidates.map((lc) => `${lc.roleId}:${lc.candidateId}`))
    return draftPickedCandidates.some((lc) => !savedSet.has(`${lc.roleId}:${lc.candidateId}`))
  }, [draftPickedCandidates, savedLockedCandidates])

  // ── Roles list for Bemanningsplan ──
  const staffingPlanRoles = useMemo(() => {
    if (roleRequirements.length > 0) {
      return roleRequirements.map((r) => ({
        id: r.id,
        quantity: r.quantity,
        roleName: r.roleName,
      }))
    }
    return (cvMatch?.rolePools ?? []).map((pool) => ({
      id: pool.roleId,
      quantity: pool.quantityRequired,
      roleName: pool.roleName,
    }))
  }, [cvMatch?.rolePools, roleRequirements])

  // ── Draft pick handlers — no network calls ──
  const handleAdd = (candidate: CandidateMatch, roleId: string) => {
    setDraftPickedCandidates((prev) => {
      if (prev.some((lc) => lc.candidateId === candidate.candidateId && lc.roleId === roleId)) {
        return prev
      }
      return [
        ...prev,
        {
          candidateId: candidate.candidateId,
          displayName: candidate.displayName,
          lockedAt: new Date().toISOString(),
          roleId,
        },
      ]
    })
  }

  const handleRemove = (candidateId: string, roleId: string) => {
    setDraftPickedCandidates((prev) =>
      prev.filter((lc) => !(lc.candidateId === candidateId && lc.roleId === roleId))
    )
  }

  const handleReset = () => {
    setDraftPickedCandidates(savedLockedCandidates)
  }

  // ── Explicit save ──
  const handleSave = () => {
    updateLocked.mutate({
      lockedCandidates: draftPickedCandidates.map((lc) => ({
        candidateId: lc.candidateId,
        displayName: lc.displayName,
        lockedAt: lc.lockedAt,
        roleId: lc.roleId,
      })),
      runId,
    })
  }

  // ── Re-run handlers ──

  const handleRerunRequest = useCallback(() => {
    setShowRerunConfirm(true)
  }, [])

  const handleRerunConfirm = useCallback(() => {
    setShowRerunConfirm(false)
    setIsRerunMode(true)
    startCvMatch.reset()
    classifyCv.reset()
    // Persist the reset to Cosmos so a page reload doesn't bring back old results.
    resetCvMatch.mutate({ runId })
  }, [startCvMatch, classifyCv, resetCvMatch, runId])

  const handleRerunCancel = useCallback(() => {
    setShowRerunConfirm(false)
  }, [])

  // When the user starts a new match, exit re-run mode on success
  const handleStartMatch = useCallback(
    (libraryId: string) => {
      setIsRerunMode(false)
      setAnalysingDocs(new Set())
      startCvMatch.mutate({ cvLibraryId: libraryId, runId })
    },
    [startCvMatch, runId]
  )

  // ── CV document sidebar handler ──
  const cvLibraryId = cvMatch?.cvLibraryId ?? ""

  const handleOpenCv = useCallback(
    (ev: RequirementEvidence) => {
      if (!ev.sourceDocument) return
      let documentUrl: string
      let title: string

      if (ev.sourceType === "tender" && ev.sourceDocId) {
        // Tender case document: served via the tender document content endpoint
        const filename = ev.sourceDocument.split("#")[0]
        const pageMatch = ev.sourceDocument.match(/#page=(\d+)/)
        const page = pageMatch ? parseInt(pageMatch[1], 10) : undefined
        documentUrl = getTenderDocumentUrl({ caseId, docId: ev.sourceDocId, filename, page })
        title = filename
      } else {
        // Library document: citation format must be "{libraryId}:{sourcepage}"
        documentUrl = getCitationFilePath(`${cvLibraryId}:${ev.sourceDocument}`, "library")
        title = ev.sourceDocument.split("#")[0]
      }

      useContentSidebarStore.getState().openPanel({
        id: "cv-document-preview",
        title,
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={title}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent
            chunkSnippet=""
            documentUrl={documentUrl}
            quoteSnippet={ev.sourceQuote}
          />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    [caseId, cvLibraryId]
  )

  // Library CV preview: citation format must be "{libraryId}:{sourcepage}" with
  // "library" as the section prefix so /api/content/library/{libraryId}:{blob} resolves correctly.
  const handlePreviewLibraryCv = useCallback(
    (libraryId: string, sourcepage: string, sourcefile: string) => {
      const documentUrl = getCitationFilePath(`${libraryId}:${sourcepage}`, "library")
      const title = sourcefile
      useContentSidebarStore.getState().openPanel({
        id: "cv-document-preview",
        title,
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={title}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent chunkSnippet="" documentUrl={documentUrl} quoteSnippet="" />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    []
  )

  const rerunDialog = (
    <AlertDialog open={showRerunConfirm} onOpenChange={setShowRerunConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("tender.cvMatch.restartTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("tender.cvMatch.restartDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleRerunCancel}>
            {t("tender.quickStart.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleRerunConfirm}>
            {t("tender.cvMatch.restartConfirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  const abortDialog = (
    <AlertDialog open={showAbortConfirm} onOpenChange={setShowAbortConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("tender.abort.cvMatch.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("tender.abort.cvMatch.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("tender.abort.keepRunning")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              abortCvMatch.mutate({ runId })
            }}
          >
            {t("tender.abort.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  // ── State 1: No classification yet ──────────────────────────────────────────
  // Show only the classify button. No table, no CV search.
  const isClassifying = classifyCv.isPending
  // noData: no result to show — either never run, or in rerun mode (query suppressed)
  const noData = !cvMatch
  const serverStatusIsPre = !cvMatchStatus || cvMatchStatus === "pending"
  // Guard: don't show State 1 while a background match is processing (polling will
  // deliver the first partial result within a few seconds).
  if (noData && (isRerunMode || serverStatusIsPre) && !startCvMatch.isPending && !isProcessing) {
    if (startCvMatch.isError) {
      return (
        <ErrorPanel
          error={
            startCvMatch.error instanceof Error
              ? startCvMatch.error.message
              : t("tender.cvMatch.unknownError")
          }
          onRetry={() => startCvMatch.reset()}
        />
      )
    }
    return (
      <>
        {rerunDialog}
        <ClassifyPanel
          canEdit={canEdit}
          canReset={isRerunMode}
          extractionCompleted={extractionCompleted}
          isClassifying={isClassifying}
          roleRequirements={roleRequirements}
          onReset={() => setIsRerunMode(false)}
          onClassify={() => {
            setIsRerunMode(false)
            classifyCv.mutate({ runId })
          }}
        />
      </>
    )
  }

  // ── Classifying spinner — no table yet ──────────────────────────────────────
  if (isClassifying) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10">
          <Loader2 className="text-primary h-10 w-10 animate-spin" />
          <div className="space-y-1 text-center">
            <h3 className="font-semibold">{t("tender.cvMatch.classifying.title")}</h3>
            <p className="text-muted-foreground text-sm">
              {t("tender.cvMatch.classifying.description")}
            </p>
          </div>
          <TenderAiDisclaimer />
        </CardContent>
      </Card>
    )
  }

  if (polledStatus === "failed" && cvMatch) {
    return (
      <>
        {rerunDialog}
        <ErrorPanel
          error={failureMessage}
          onRetry={() => {
            if (cvLibraryId) {
              handleStartMatch(cvLibraryId)
              return
            }
            handleRerunRequest()
          }}
        />
      </>
    )
  }

  // ── States 2 + 3 + processing: unified table view ───────────────────────────
  // Shown as soon as classification is done (cvMatch exists). The CvSourcePanel
  // stays visible in all states so users can always add more CVs. isPopulating
  // controls the empty-row message; processing shows an inline spinner in the header.
  if (cvMatch) {
    const hasAnyCandidates = cvMatch.rolePools.some((p) => p.candidates.length > 0)
    const showPopulating = isProcessing || !hasAnyCandidates

    const cvListEntries = buildCvListEntries(cvDocuments, cvMatch.rolePools, analysingDocs)

    return (
      <>
        {rerunDialog}
        {abortDialog}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
          <div className="min-w-0 space-y-6">
            {/* CV source panel — always visible so users can keep adding CVs */}
            <CvSourcePanel
              canEdit={canEdit}
              caseId={caseId}
              cvLibraryId={cvLibraryId}
              cvLibraryKeyword={cvLibraryKeyword}
              cvListEntries={cvListEntries}
              isAutoMatching={isProcessing}
              isUploadingCv={isUploadingCv}
              runId={runId}
              onAutoMatch={handleStartMatch}
              onDocAnalysisEnd={removeAnalysing}
              onDocAnalysisStart={addAnalysing}
              onPreviewCv={handlePreviewLibraryCv}
              onRemoveCvDocument={onRemoveCvDocument}
              onUploadCvDocument={onUploadCvDocument}
            />

            {isProcessing &&
            cvMatch.rateLimitRetry?.nextRetryAt &&
            !Number.isNaN(Date.parse(cvMatch.rateLimitRetry.nextRetryAt)) ? (
              <div className="border-warning/30 bg-warning/10 text-warning-foreground rounded-md border px-3 py-2 text-xs">
                {t("tender.rateLimit.waiting", {
                  time: new Date(cvMatch.rateLimitRetry.nextRetryAt).toLocaleString(i18n.language, {
                    dateStyle: "short",
                    timeStyle: "short",
                  }),
                })}
              </div>
            ) : null}

            {/* Candidate pools table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">
                    {t("tender.cvMatch.candidatePoolsHeading")}
                  </h2>
                  {isProcessing && (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {isAbortPending
                        ? t("tender.abort.pending")
                        : t("tender.cvMatch.searchingCandidates")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isDirty && (
                    <Badge className="border-warning bg-warning/10 text-warning-foreground">
                      {t("tender.cvMatch.notSavedBadge")}
                    </Badge>
                  )}
                  {canEdit && isProcessing && (
                    <Button
                      className="hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
                      disabled={isAbortPending}
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAbortConfirm(true)}
                    >
                      {isAbortPending ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          {t("tender.abort.pending")}
                        </>
                      ) : (
                        <>
                          <StopCircle className="mr-2 h-4 w-4" />
                          {t("tender.abort.action")}
                        </>
                      )}
                    </Button>
                  )}
                  {!isProcessing && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void downloadCvMatchDocx(caseId, runId, caseTitle)}
                      >
                        <FileDown className="mr-2 h-3 w-3" />
                        {t("tender.cvMatch.exportDocx")}
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={handleRerunRequest}>
                          <RotateCcw className="mr-2 h-3 w-3" />
                          {t("tender.cvMatch.restartButton")}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <TenderAiDisclaimer />

              {cvMatch.rolePools.map((pool) => (
                <RolePoolCard
                  key={pool.roleId}
                  caseId={caseId}
                  cvLibraryId={cvLibraryId}
                  isPopulating={showPopulating}
                  pickedForRole={draftPickedCandidates.filter((lc) => lc.roleId === pool.roleId)}
                  pool={pool}
                  savedForRole={savedLockedCandidates.filter((lc) => lc.roleId === pool.roleId)}
                  onAdd={canEdit ? handleAdd : undefined}
                  onOpenCv={handleOpenCv}
                  onRemove={canEdit ? handleRemove : undefined}
                  onRemoveCandidate={
                    canEdit
                      ? (candidateId) => removeCandidate.mutate({ runId, candidateId })
                      : undefined
                  }
                />
              ))}
            </div>
          </div>

          <StaffingPlan
            isDirty={isDirty}
            isSaving={updateLocked.isPending}
            pickedCandidates={draftPickedCandidates}
            roles={staffingPlanRoles}
            savedCandidates={savedLockedCandidates}
            onRemove={canEdit ? handleRemove : undefined}
            onReset={canEdit ? handleReset : undefined}
            onSave={canEdit ? handleSave : undefined}
          />
        </div>
      </>
    )
  }

  return null
}
